import {
  Box,
  FormControl,
  FormErrorMessage,
  Input,
  Textarea,
  Checkbox,
  Radio,
  RadioGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  VStack,
} from '@chakra-ui/react';

import * as Sentry from '@sentry/nextjs';

import { Formik, Form, Field, FormikErrors, FormikTouched } from 'formik';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import fetchJsonp from 'fetch-jsonp';
import Cookies from 'js-cookie';

import { PrimaryButton } from '../LightLP/Button';
import ChevronDown from '../Icons/ChevronDown';

import { useGetUtmCookies } from '../../hooks/utmCookies';

import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

const ContactSchema = {
  firstName: Yup.string()
    .min(1, 'Too Short!')
    .max(50, 'Too Long!')
    .trim()
    .required('First Name is required'),
  lastName: Yup.string()
    .min(1, 'Too Short!')
    .max(50, 'Too Long!')
    .trim()
    .required('Last Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string().min(6, 'Too Short!').max(20, 'Too Long!').required('Phone is required'),
  jobTitle: Yup.string()
    .min(1, 'Too Short!')
    .max(50, 'Too Long!')
    .trim()
    .required('Job Title is required'),
  company: Yup.string()
    .min(1, 'Too Short!')
    .max(50, 'Too Long!')
    .trim()
    .required('Company is required'),
  companyHqCountry: Yup.string().required('Company HQ Country is required'),
  help: Yup.string(),
  subscribe: Yup.boolean(),
};

interface FormProps {
  title: string;
  text?: string;
  theme: {
    green100?: string;
    green200?: string;
    green300?: string;
  };
  buttonText: string;
  rounded: boolean;
  completed: boolean;
}

interface Answer {
  country: string;
  experience: string;
  skills: string[];
  education: string;
  benefits: string;
}

const companyHqCountryOptions = ['United Kingdom', 'United States', 'Canada', 'Other'];

export default function BuildTeamContactHandler({
  title,
  text,
  theme,
  buttonText,
  rounded = true,
  completed,
}: FormProps) {
  const FormSchema = Yup.object().shape(ContactSchema);
  const router = useRouter();
  const [submitError, setSubmitError] = useState<string>('');
  const [companyHqCountryValue, setCompanyHqCountryValue] = useState<string>('Company HQ Country');
  const [answer, setAnswer] = useState<Answer>({
    country: '',
    experience: '',
    skills: [],
    education: '',
    benefits: '',
  });

  const utmCookies = useGetUtmCookies() ?? '';
  const utmParams = utmCookies ? '&' + utmCookies.replace(/\?/g, '') : '';

  useEffect(() => {
    if (sessionStorage?.salaryCalculator && completed) {
      const parsedAnswer = JSON.parse(sessionStorage.salaryCalculator);
      setAnswer({
        country: parsedAnswer.country.name,
        experience: parsedAnswer.experienceLevel.name,
        skills: parsedAnswer.codeLanguages.map((skill) => skill.name),
        education: parsedAnswer.educationLevel.name,
        benefits: parsedAnswer.benefitsLevel.name,
      });
    }
  }, [completed]);

  const FormInput = ({ type, placeholder, ...field }: { type: string; placeholder: string }) => {
    return (
      <Input
        type={type}
        placeholder={placeholder}
        borderColor="grey.400"
        borderRadius="0.25rem"
        bgColor="#fff"
        _placeholder={{ color: 'grey.500' }}
        focusBorderColor={theme.green200}
        {...field}
      />
    );
  };

  return (
    <Box
      px={{ base: '4', lg: '0' }}
      py={{ base: '6', lg: '0' }}
      borderRadius="1rem"
      maxW="29.5rem"
      mx="auto"
    >
      {title && (
        <Box
          fontSize="2rem"
          line-height="2.25rem"
          fontWeight="bold"
          color="grey.900"
          mb="6"
          textAlign="center"
        >
          {title}
        </Box>
      )}
      {text && (
        <Box line-height="1.25rem" color="grey.700" mb="1">
          {text}
        </Box>
      )}
      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          jobTitle: '',
          company: '',
          companyHqCountry: '',
          help: '',
          subscribe: false,
        }}
        validationSchema={FormSchema}
        onSubmit={async (values, actions) => {
          try {
            actions.setSubmitting(true);

            await fetch('/api/team-builder', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                // form values
                first_name: values.firstName,
                last_name: values.lastName,
                email: values.email,
                phone_number: values.phone,
                title: values.jobTitle,
                company: values.company,
                company_hq_country: values.companyHqCountry,
                help: values.help,
                subscribe: values.subscribe.toString(),
                country: answer.country,
                experience: answer.experience,
                skills: answer.skills.toString(),
                education: answer.education,
                benefits: answer.benefits,
                gclid: (router.query.gclid as string) ?? null,
                // tracking values
                fbc: Cookies.get('_fbc') ?? null,
                fbp: Cookies.get('_fbp') ?? null,
                form_type: 'Salary Calculator',
                utmParams,
              }),
            }).then((response) => {
              if (response.ok) return response.json();
              return Promise.reject(response);
            });

            sessionStorage.salaryCalculatorSubmitted = true;
            router.replace(
              {
                query: { ...router.query, s: '1' },
              },
              undefined,
              { shallow: true },
            );
          } catch (error: unknown) {
            actions.setSubmitting(false);
            setSubmitError('Form submission failed. Please, try again later.');
            Sentry.captureException(error);
          }
        }}
      >
        {({
          errors,
          touched,
          isSubmitting,
        }: {
          errors: FormikErrors<{
            firstName: string;
            lastName: string;
            email: string;
            phone: string;
            jobTitle: string;
            company: string;
            companyHqCountry: string;
            help: string;
            subscribe: boolean;
          }>;
          touched: FormikTouched<{
            firstName: string;
            lastName: string;
            email: string;
            phone: string;
            jobTitle: string;
            company: string;
            companyHqCountry: string;
            help: string;
            subscribe: boolean;
          }>;
          isSubmitting: boolean;
        }) => (
          <Form>
            <Box maxW={{ lg: '25rem' }} mx="auto">
              <VStack spacing="4">
                <Field name="firstName">
                  {({ field }) => (
                    <FormControl isInvalid={errors.firstName && touched.firstName}>
                      <FormInput type="text" placeholder="First Name" {...field} />
                      <FormErrorMessage mt="px">{errors.firstName}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="lastName">
                  {({ field }) => (
                    <FormControl isInvalid={errors.lastName && touched.lastName}>
                      <FormInput type="text" placeholder="Last Name" {...field} />
                      <FormErrorMessage mt="px">{errors.lastName}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="email">
                  {({ field }) => (
                    <FormControl isInvalid={errors.email && touched.email}>
                      <FormInput type="email" placeholder="Email" {...field} />
                      <FormErrorMessage mt="px">{errors.email}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="phone">
                  {({ field }) => (
                    <FormControl isInvalid={errors.phone && touched.phone}>
                      <FormInput type="number" placeholder="Phone" {...field} />
                      <FormErrorMessage mt="px">{errors.phone}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="jobTitle">
                  {({ field }) => (
                    <FormControl isInvalid={errors.jobTitle && touched.jobTitle}>
                      <FormInput type="text" placeholder="Job Title" {...field} />
                      <FormErrorMessage mt="px">{errors.jobTitle}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="company">
                  {({ field }) => (
                    <FormControl isInvalid={errors.company && touched.company}>
                      <FormInput type="text" placeholder="Company" {...field} />
                      <FormErrorMessage mt="px">{errors.company}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </VStack>
              <Box mt="1rem">
                <Menu flip={false} gutter={4} matchWidth>
                  {({ isOpen }) => (
                    <>
                      <MenuButton
                        as={Button}
                        rightIcon={
                          <ChevronDown
                            transform={{
                              base: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                            }}
                            transition="transform .3s ease-in-out"
                            color="grey.900"
                          />
                        }
                        bgColor="white"
                        borderRadius="0.25rem"
                        borderWidth="0.0625rem"
                        boxShadow={
                          errors.companyHqCountry && touched.companyHqCountry && '0 0 0 1px #e53e3e'
                        }
                        borderColor={
                          errors.companyHqCountry && touched.companyHqCountry ? 'red' : 'grey.400'
                        }
                        color={companyHqCountryValue === 'Company HQ Country' && 'grey.500'}
                        fontWeight="normal"
                        w="100%"
                        textAlign="left"
                        _hover={{ bg: 'grey.200' }}
                        _active={{ bg: 'grey.200' }}
                        _expanded={{ bg: 'white' }}
                      >
                        {companyHqCountryValue}
                      </MenuButton>
                      <RadioGroup
                        as={MenuList}
                        onChange={setCompanyHqCountryValue}
                        value={companyHqCountryValue}
                        boxShadow="0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)"
                        borderRadius="0.25rem"
                        minWidth="100%"
                        py="1rem"
                        px="1rem"
                      >
                        {companyHqCountryOptions.map((option) => (
                          <MenuItem
                            as="div"
                            key={option}
                            _hover={{
                              bgColor: 'grey.200',
                            }}
                            p="0"
                            sx={{
                              '.chakra-radio__control': {
                                display: 'none',
                              },
                            }}
                          >
                            <Field
                              name="companyHqCountry"
                              as={Radio}
                              value={option}
                              w="100%"
                              p="0.625rem"
                            >
                              {option}
                            </Field>
                          </MenuItem>
                        ))}
                      </RadioGroup>
                    </>
                  )}
                </Menu>
                <FormControl isInvalid={errors.companyHqCountry && touched.companyHqCountry}>
                  <FormErrorMessage mt="px">{errors.companyHqCountry}</FormErrorMessage>
                </FormControl>
              </Box>
              <Field name="help">
                {({ field }) => (
                  <FormControl>
                    <Textarea
                      mt="4"
                      placeholder="How can we help build your team?"
                      borderColor="grey.400"
                      borderRadius="0.25rem"
                      bgColor="#fff"
                      _placeholder={{ color: 'grey.500' }}
                      focusBorderColor={theme.green200}
                      {...field}
                    />
                  </FormControl>
                )}
              </Field>
              <Field name="subscribe">
                {({ field }) => (
                  <FormControl mt="1rem">
                    <Checkbox colorScheme="grey" {...field}>
                      <Box fontSize="sm" color="grey.500" lineHeight="1.25">
                        I would like to receive communications from Terminal.
                      </Box>
                    </Checkbox>
                  </FormControl>
                )}
              </Field>
              <PrimaryButton
                display="block"
                mx="auto"
                mt="6"
                w={{ base: '100%', lg: 'auto' }}
                type="submit"
                theme={theme}
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                borderRadius={rounded ? '0.25rem' : '0'}
                _hover={{
                  backgroundColor: theme.green300,
                  boxShadow: rounded ? '0 0.25rem 0.5rem rgba(0, 0, 0, 0.12)' : 'none',
                }}
              >
                {buttonText}
              </PrimaryButton>
              {submitError && (
                <Box fontSize="xs" mt="1" color="red">
                  {submitError}
                </Box>
              )}
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
